/**
 * EPIC-DM01: Demo Mode Progress
 *
 * Three display modes:
 *   • steps  – numbered circles with connecting lines
 *   • bar    – horizontal percentage bar with label
 *   • circle – large SVG ring with centred percentage
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

/* ================================================================== */
/*  Shared Types                                                      */
/* ================================================================== */

interface StepItem {
  label: string;
  status: 'pending' | 'active' | 'complete';
}

interface DemoProgressBaseProps {
  className?: string;
  /** Optional time-estimate text shown below the visualisation */
  timeEstimate?: string;
}

export interface DemoProgressStepsProps extends DemoProgressBaseProps {
  mode: 'steps';
  steps: StepItem[];
}

export interface DemoProgressBarProps extends DemoProgressBaseProps {
  mode: 'bar';
  /** 0 – 100 */
  value: number;
  label?: string;
}

export interface DemoProgressCircleProps extends DemoProgressBaseProps {
  mode: 'circle';
  /** 0 – 100 */
  value: number;
  label?: string;
}

export type DemoProgressProps =
  | DemoProgressStepsProps
  | DemoProgressBarProps
  | DemoProgressCircleProps;

/* ================================================================== */
/*  Sub-components                                                    */
/* ================================================================== */

/** Step-mode: numbered circles with connecting lines */
function StepsProgress({ steps, className }: { steps: StepItem[]; className?: string }) {
  return (
    <div className={cn('flex items-center w-full', className)}>
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;

        const circleClass = cn(
          'flex items-center justify-center rounded-full w-10 h-10 text-demo-base font-bold shrink-0',
          step.status === 'complete' && 'bg-demo-green text-white',
          step.status === 'active' && 'bg-demo-gold text-white',
          step.status === 'pending' && 'bg-gray-200 text-demo-text-muted',
        );

        const lineClass = cn(
          'flex-1 h-1 mx-2 rounded',
          step.status === 'complete' ? 'bg-demo-green' : 'bg-gray-200',
        );

        return (
          <React.Fragment key={idx}>
            <div className="flex flex-col items-center gap-1">
              <div className={circleClass} aria-label={`Step ${idx + 1}: ${step.label}`}>
                {step.status === 'complete' ? '✓' : idx + 1}
              </div>
              <span className="text-[14px] text-demo-text-muted text-center max-w-[80px] leading-tight">
                {step.label}
              </span>
            </div>
            {!isLast && <div className={lineClass} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Bar-mode: horizontal percentage bar */
function BarProgress({
  value,
  label,
  className,
}: {
  value: number;
  label?: string;
  className?: string;
}) {
  const clamped = Math.min(100, Math.max(0, value));

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <div className="flex justify-between mb-2 text-demo-base">
          <span className="font-semibold text-demo-text">{label}</span>
          <span className="text-demo-text-muted">{clamped}%</span>
        </div>
      )}
      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-demo-green rounded-full transition-all duration-500"
          style={{ width: `${clamped}%` }}
          role="progressbar"
          aria-valuenow={clamped}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

/** Circle-mode: SVG ring with centred percentage */
function CircleProgress({
  value,
  label,
  className,
}: {
  value: number;
  label?: string;
  className?: string;
}) {
  const clamped = Math.min(100, Math.max(0, value));
  const size = 120;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={stroke}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--demo-green)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-500"
        />
      </svg>

      {/* Centred label overlay */}
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-demo-2xl font-bold text-demo-text">{clamped}%</span>
        {label && <span className="text-[14px] text-demo-text-muted">{label}</span>}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Main Component                                                    */
/* ================================================================== */

const DemoProgress = React.forwardRef<HTMLDivElement, DemoProgressProps>(
  (props, ref) => {
    const { className, timeEstimate, ...rest } = props;

    return (
      <div ref={ref} className={cn('relative flex flex-col items-center gap-3', className)}>
        {rest.mode === 'steps' && <StepsProgress steps={rest.steps} />}
        {rest.mode === 'bar' && <BarProgress value={rest.value} label={rest.label} />}
        {rest.mode === 'circle' && <CircleProgress value={rest.value} label={rest.label} />}

        {timeEstimate && (
          <p className="text-[14px] text-demo-text-muted italic">{timeEstimate}</p>
        )}
      </div>
    );
  },
);

DemoProgress.displayName = 'DemoProgress';

export { DemoProgress };
